import { FC } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Input, Form, message } from 'antd';
import Title from 'antd/lib/typography/Title';
import { getLocationsApi } from 'api';
import { useStore } from 'routes/inventorize_meter/useStore';
import { useQuery } from 'react-query';
import { Layout } from 'components/Layout/Layout';
import { useSessionStore } from 'store/session';
import { SerializedInventoryLocation } from '@enmon/commons/lib/tools';
import { QUERY_KEYS } from 'utils/queryKeys';
import { getSuggestionsDbApi } from 'api/indexed_db/suggestions';
import { isEmpty } from 'rambda';
import { captureException } from '@sentry/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '../../components/Button/Button';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { FORM_LAYOUT_CONFIG } from '../../consts/common';
import { FullWidthHeightSpace } from '../../components/Space/FullWidthHeightSpace';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { AuthPage as messages } from './messages';

const FormItem = Form.Item;

interface FormValues {
  locationApiKey: SerializedInventoryLocation['api_key'];
  email: string;
}

export const AuthPage: FC = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { isOnline } = useOnlineStatus();
  const intl = useIntl();

  const { setLocation } = useStore();
  const suggestionsDb = getSuggestionsDbApi();

  const [form] = Form.useForm<FormValues>();

  const { apiKey, email } = useSessionStore();

  const { isFetching: isLoading, refetch } = useQuery(
    QUERY_KEYS.auth(apiKey),
    async ({ signal }) => {
      const { id } = await getLocationsApi().validateApiKey(apiKey, signal);
      return await getLocationsApi().getOne<{ data: SerializedInventoryLocation }>(id, signal);
    },
    {
      enabled: isOnline && !!apiKey && !!email,
      cacheTime: 0,
      retry: false,
      onError: err => {
        console.error(err);
        captureException(err);
        message.error({
          content: isEmpty(apiKey)
            ? intl.formatMessage(messages.missingApiKey)
            : intl.formatMessage(messages.validationFailed),
          key: 'AuthError',
        });
        // NOTE: Reset suggestion db when auth fails
        suggestionsDb._clearData();
      },
      onSuccess: response => {
        useSessionStore.setState({ isAuthenticated: true });
        setLocation(response.data);
        const searchParams = new URLSearchParams(search);
        searchParams.delete('apiKey');
        navigate({ search: searchParams.toString() }, { replace: true });
      },
    },
  );

  const handleOnFinish = (values: FormValues) => {
    useSessionStore.setState({ apiKey: values.locationApiKey, email: values.email });
    // NOTE: it makes login button look working if mad user click it multiple times with same wrong credentials
    if (values.locationApiKey === apiKey && values.email === email) refetch();
  };

  // Page to block user enter app without any apiKey
  return (
    <Layout
      footerContent={
        <Button block form="authForm" htmlType="submit" loading={isLoading} type="primary">
          <FormattedMessage {...messages.next} />
        </Button>
      }
      headerContent={<FormattedMessage {...messages.headerContent} />}
    >
      {isLoading ? (
        <LoadingSpinner message={intl.formatMessage(messages.loadingMessage)} />
      ) : (
        <FullWidthHeightSpace align="center" direction="vertical">
          {!isOnline && (
            <Title level={5} style={{ textAlign: 'center' }} type="warning">
              <FormattedMessage {...messages.offlineMessage} />
            </Title>
          )}
          {!apiKey && (
            <Title level={5} style={{ textAlign: 'center' }}>
              <FormattedMessage {...messages.apiKeyInfo} />
            </Title>
          )}
          <Form
            form={form}
            id="authForm"
            layout="horizontal"
            name="authForm"
            onFinish={handleOnFinish}
            {...FORM_LAYOUT_CONFIG}
          >
            <FormItem
              initialValue={apiKey ?? ''}
              label={<FormattedMessage {...messages.apiKey} />}
              name="locationApiKey"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage(messages.requiredValue),
                },
              ]}
            >
              <Input placeholder={intl.formatMessage(messages.apiKeyPlaceholder)} />
            </FormItem>
            <FormItem
              initialValue={email ?? ''}
              label={<FormattedMessage {...messages.email} />}
              name="email"
              rules={[
                {
                  required: true,
                  type: 'email',
                  message: <FormattedMessage {...messages.requiredValue} />,
                },
              ]}
            >
              <Input placeholder={intl.formatMessage(messages.emailPlaceholder)} type="email" />
            </FormItem>
          </Form>
        </FullWidthHeightSpace>
      )}
    </Layout>
  );
};
